import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Products.css';
import productsData from '../../data/products.json';

const Carousel = ({ items, title, alignment = 'center' }) => {
  const trackRef = useRef(null);
  const [scrollX, setScrollX] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const visibleCount = 5; // show up to 5 cards in the viewport

  // compute step dynamically based on card width + gap
  function computeStep() {
    if (!trackRef.current) return 300;
    const track = trackRef.current;
    const trackStyle = window.getComputedStyle(track);
    // try to read CSS vars --card-w and --card-gap from the track; fall back to measuring a card
    let cardW = parseFloat(trackStyle.getPropertyValue('--card-w')) || 0;
    let gap = parseFloat(trackStyle.getPropertyValue('--card-gap')) || 0;

    if (!cardW || !gap) {
      const card = track.querySelector('.product-card');
      if (card) {
        const cardStyle = window.getComputedStyle(card);
        cardW = cardW || parseFloat(cardStyle.getPropertyValue('flex-basis')) || parseFloat(cardStyle.width) || 220;
        const second = card.nextElementSibling;
        if (second) {
          gap = gap || (second.offsetLeft - (card.offsetLeft + card.offsetWidth));
        }
      }
    }

    if (!cardW) cardW = 220;
    if (!gap || gap <= 0) gap = 16;
    return Math.round((cardW + gap) * visibleCount);
  }

  // Check scroll position to enable/disable buttons
  const updateScrollButtons = () => {
    if (!trackRef.current) return;
    const track = trackRef.current;
    const scrollLeft = track.scrollLeft;
    const maxScroll = track.scrollWidth - track.clientWidth;
    
    setCanScrollLeft(scrollLeft > 5); // Small threshold for floating point precision
    setCanScrollRight(scrollLeft < maxScroll - 5);
  };

  // pointer drag-to-scroll
  const [isDragging, setIsDragging] = useState(false);
  
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let isDown = false;
    let startX, scrollLeft;
    let hasMoved = false;
    const dragThreshold = 6; // pixels

    const onPointerDown = (e) => {
      isDown = true;
      hasMoved = false;
      startX = e.pageX || e.touches?.[0]?.pageX;
      scrollLeft = el.scrollLeft;
      e.preventDefault();
    };

    const onPointerMove = (e) => {
      if (!isDown) return;
      const x = e.pageX || e.touches?.[0]?.pageX;
      const walk = (startX - x);
      if (Math.abs(walk) > dragThreshold) {
        hasMoved = true;
        setIsDragging(true);
        el.classList.add('dragging');
      }
      el.scrollLeft = scrollLeft + walk;
    };

    const onPointerUp = () => {
      isDown = false;
      el.classList.remove('dragging');
      // Keep isDragging true for a short time to prevent accidental clicks
      if (hasMoved) {
        setTimeout(() => {
          setIsDragging(false);
        }, 100);
      } else {
        setIsDragging(false);
      }
    };

    const onScroll = () => {
      updateScrollButtons();
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('scroll', onScroll);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    // Initial check
    updateScrollButtons();

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  function scrollBy(amount) {
    if (!trackRef.current) return;
    trackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    // Update buttons after scroll completes
    setTimeout(updateScrollButtons, 300);
  }

  return (
    <div className="product-row">
      <h3 className="row-title">{title}</h3>
      <div className="row-controls">
        <button 
          aria-label="Sola kaydır" 
          className="row-btn left" 
          disabled={!canScrollLeft || items.length <= visibleCount} 
          onClick={() => scrollBy(-computeStep())}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div ref={trackRef} className={`product-track ${alignment === 'left' ? 'track-left-aligned' : ''}`}>
          {items.map(p => {
            // Görsel import fonksiyonu - ProductDetail'deki ile aynı
            const getImagePath = (imageName) => {
              if (!imageName) return null;
              
              // Önce dosya uzantısını kontrol et
              if (imageName && imageName.includes('.')) {
                try {
                  return require(`../Images/ProductImages/${imageName}`);
                } catch (err) {
                  console.log('Image not found with extension:', imageName);
                  return null;
                }
              }
              
              // Uzantı yoksa önce .png sonra .jpg dene
              try {
                return require(`../Images/ProductImages/${imageName}.png`);
              } catch (err) {
                try {
                  return require(`../Images/ProductImages/${imageName}.jpg`);
                } catch (err2) {
                  console.log('Image not found for:', imageName);
                  return null;
                }
              }
            };

            let imgSrc = getImagePath(p.image);
            // Fallback olarak public klasöründeki resmi kullan
            if (!imgSrc) {
              imgSrc = `${process.env.PUBLIC_URL}/${p.image}`;
            }

            return (
              <Link to={`/product/${p.id}`} key={p.id} className="product-card-link" onClick={(ev) => {
                // prevent navigation if user was dragging
                if (isDragging) {
                  ev.preventDefault();
                  ev.stopPropagation();
                }
              }}>
                <article className="product-card">
                  <div className="product-thumb" style={{ backgroundImage: `url(${imgSrc})` }} />
                  <div className="product-title">{p.name}</div>
                </article>
              </Link>
            );
          })}
        </div>
        <button 
          aria-label="Sağa kaydır" 
          className="row-btn right" 
          disabled={!canScrollRight || items.length <= visibleCount} 
          onClick={() => scrollBy(computeStep())}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const Products = () => {
  const [products] = useState(productsData);

  const best = products.filter(p => p.bestSeller); // Show all best seller products
  const featured = products.filter(p => p.featured); // Show all featured products
  const discounted = products.filter(p => p.discounted); // Show all discounted products

  return (
    <section className="products-section container page-content">
      <h2 className="section-main">Ürünlerimiz</h2>
      <div className="products-wrap">
        <Carousel items={best} title="En Çok Satanlar" />
        <Carousel items={featured} title="Öne Çıkan Ürünler" />
        <Carousel items={discounted} title="İndirimli Ürünler" alignment="left" />
      </div>
    </section>
  );
};

export default Products;